import { useState, useCallback, useEffect, useRef } from 'react';
import Form, { Item, Label, ButtonItem, ButtonOptions, RequiredRule, FormTypes } from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import { formatMessage } from 'devextreme/localization';
import { useAppSelector, useAppDispatch } from '../../../contexts/hooks';
import { setLoading } from '../../../contexts/slices/auth';
import useApi, { ACCESS_BLOCKED_CODE, LOGIN_BLOCKED_CODE } from './LoginForm.api';

import { LoginStruct } from '../../../types/types.user';
import { useApiInfo } from '../../../api/api.info';
import { appVersion } from '../../../version';
import { runApWithoutToken } from '../../../utils/axios-pettitions';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const formData: LoginStruct = { User: '', Password: '' };

export const LoginForm = () => {
  const [formInstance, setCormInstance] = useState<FormTypes.InitializedEvent>();
  const { login } = useApi();
  const dispatch = useAppDispatch();
  const [lstLocations, setLstLocation] = useState<any>([]);
  const loading = useAppSelector((data) => data.userInfo).Loading;
  const isAuthenticated = useAppSelector((data) => data.configuration.IsAuthenticated);
  const Locations = useAppSelector((data) => data.userInfo).PermissionsCampus;
  const systemLocations = useAppSelector((data) => data.configuration.Data?.ComboBox)?.find((x) => x.Name === 'SysI_Sedes')?.Data;
  const labName = useAppSelector((data) => data.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'IntitutionName')[0])?.Cgn_Resultado || '';
  const formRef = useRef<Form>(null);
  const { getInfo } = useApiInfo();
  const [infoVersion, setInfoVersion] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string>('');
  const [isTrustedIP, setIsTrustedIP] = useState<boolean>(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const [blockMessage, setBlockMessage] = useState<string>('');
  const [permanentBlock, setPermanentBlock] = useState<boolean>(false);

  const isBlocked = permanentBlock || blockedUntil !== null;

  // Cuenta regresiva del bloqueo temporal: al llegar a cero se rehabilita el formulario solo.
  useEffect(() => {
    if (blockedUntil === null) return;

    const tick = () => {
      const secs = Math.ceil((blockedUntil - Date.now()) / 1000);
      if (secs <= 0) {
        setBlockedUntil(null);
        setRemainingSeconds(0);
        setBlockMessage('');
      } else {
        setRemainingSeconds(secs);
      }
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [blockedUntil]);

  const formatRemaining = (secs: number) => {
    if (secs < 60) return `${secs}s`;
    const min = Math.floor(secs / 60);
    const rest = secs % 60;
    return `${min}:${String(rest).padStart(2, '0')} min`;
  };

  // Verificar si la IP es de confianza y cargar config de Turnstile
  useEffect(() => {
    const init = async () => {
      try {
        // Verificar IP de confianza - el backend compara la IP del request contra las IPs registradas
        const ipRes = await runApWithoutToken('Authentication/GetTrustedIPs', 'GET');
        if (ipRes.internalCode < 200 && Array.isArray(ipRes.data) && ipRes.data.length > 0) {
          setIsTrustedIP(true);
          return; // IP de confianza, no cargar captcha
        }
      } catch {}

      // IP no confiable, cargar captcha
      try {
        const configRes = await fetch('/config.json');
        const config = await configRes.json();
        const siteKey = config.turnstileSiteKey;
        if (!siteKey) return;
        setTurnstileSiteKey(siteKey);

        if (document.getElementById('cf-turnstile-script')) return;
        const script = document.createElement('script');
        script.id = 'cf-turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        document.head.appendChild(script);
      } catch {}
    };

    init();
  }, []);

  // Renderizar widget cuando el script esté listo y tengamos el siteKey
  useEffect(() => {
    if (!turnstileSiteKey || !turnstileRef.current) return;

    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current) return;
      // Limpiar widget anterior si existe
      if (turnstileWidgetId.current) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch {}
      }
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token: string) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(null),
        'error-callback': () => setCaptchaToken(null),
        theme: 'light',
      });
    };

    if (window.turnstile) {
      renderWidget();
    } else {
      const script = document.getElementById('cf-turnstile-script');
      if (script) script.addEventListener('load', renderWidget);
    }

    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch {}
        turnstileWidgetId.current = null;
      }
    };
  }, [turnstileSiteKey]);

  // Efecto que se ejecuta solo al montar el componente para limpiar loading
  useEffect(() => {
    // Limpiar el estado de loading al cargar/recargar la página
    dispatch(setLoading(false));
  }, []); // Array vacío significa que solo se ejecuta una vez al montar

  useEffect(() => {
    const form = formRef.current?.instance;
    if (form) {
      form.updateData('User', '');
      form.updateData('Password', '');
      form.updateData('Campus', null);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!Locations || Locations.length <= 1) return;

    // El login (código 102) devuelve las sedes del usuario con la entidad Sede
    // anidada. Si el backend aún no la envía, se resuelven contra el ComboBox.
    let nuevaLista = Locations.map((location) => location.Sede).filter((sede) => !!sede);

    if (nuevaLista.length === 0 && systemLocations) {
      const filteredLocations = Locations.map((location) => location.Rus_Sd_Id);
      nuevaLista = systemLocations.filter((item) => filteredLocations.includes(item.Sd_Id));
    }

    if (nuevaLista.length === 0) return;

    setLstLocation(nuevaLista);
    formInstance?.component?.getEditor('Campus')?.focus();
  }, [Locations]);

  useEffect(() => {
    const userInstance = formInstance?.component?.getEditor('User');
    if (userInstance) {
      userInstance.focus();
    }
  }, [formInstance]);

  useEffect(() => {
    getInfo()
      .then((res) => {
        setInfoVersion(res);
      })
      .catch((error) => {
        console.error('Error fetching version info:', error);
      });
  }, []);

  // Función para deshabilitar el autocompletado después del renderizado
  useEffect(() => {
    const timer = setTimeout(() => {
      // Buscar todos los inputs de password y aplicar atributos adicionales
      const passwordInputs = document.querySelectorAll('input[type="password"]');
      passwordInputs.forEach((input) => {
        input.setAttribute('autocomplete', 'new-password');
        input.setAttribute('data-form-type', 'other');
      });

      // También aplicar a inputs de texto que puedan ser detectados como usuario
      const textInputs = document.querySelectorAll('input[type="text"]');
      textInputs.forEach((input: any) => {
        if (input.name === 'User' || input.getAttribute('data-field') === 'User') {
          input.setAttribute('autocomplete', 'off');
          input.setAttribute('data-form-type', 'other');
        }
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [formInstance]);

  const onInitialized = (e: FormTypes.InitializedEvent) => {
    setCormInstance(e);
  };

  const resetTurnstile = useCallback(() => {
    setCaptchaToken(null);
    if (turnstileWidgetId.current && window.turnstile) {
      try { window.turnstile.reset(turnstileWidgetId.current); } catch {}
    }
  }, []);

  const captchaRequired = !!turnstileSiteKey && !isTrustedIP;

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (captchaRequired && !captchaToken) {
        return;
      }

      if (isBlocked) {
        return;
      }

      try {
        // Hacer una copia de los datos antes de enviar
        const loginData = { ...formData, CaptchaToken: captchaRequired ? captchaToken : null };
        const result = await login(loginData);

        // Demasiados intentos fallidos: se bloquea el formulario hasta que expire el tiempo.
        if (result.code === LOGIN_BLOCKED_CODE) {
          setBlockMessage(result.message);
          setBlockedUntil(Date.now() + (result.blockedSeconds || 0) * 1000);
        }

        // Acceso bloqueado por el administrador: no hay tiempo de espera, requiere soporte.
        if (result.code === ACCESS_BLOCKED_CODE) {
          setBlockMessage(result.message);
          setPermanentBlock(true);
        }

        // El token de Turnstile es de un solo uso: cualquier intento lo consume.
        // Si el login no terminó en sesión iniciada (credenciales erradas, o
        // código 102 pendiente de elegir sede), hay que generar un token nuevo
        // o el siguiente intento fallará por captcha aunque los datos sean correctos.
        if (captchaRequired && result.code !== 100) {
          resetTurnstile();
        }
      } catch (error) {
        // En caso de error, también limpiar para seguridad
        resetTurnstile();
        setTimeout(() => {
          const form = formRef.current?.instance;
          if (form) {
            form.updateData('Password', '');
          }
        }, 100);
      }
    },
    [login, captchaToken, captchaRequired, resetTurnstile, isBlocked]
  );

  const changePasswordMode = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div>
      {/* Múltiples campos señuelo para confundir a los navegadores */}
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }}>
        <input type='text' name='fakeusernameremembered' tabIndex={-1} />
        <input type='password' name='fakepasswordremembered' tabIndex={-1} />
      </div>

      <form className='login-form' onSubmit={onSubmit} autoComplete='off' data-form-type='login'>
        {/* Campo tipo contraseña engaño mejorado */}
        <input
          type='password'
          name='password_fake'
          style={{
            display: 'none',
            position: 'absolute',
            left: '-9999px',
            top: '-9999px',
          }}
          tabIndex={-1}
          autoComplete='new-password'
        />

        {isBlocked && (
          <div
            role='alert'
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 12px',
              marginBottom: '12px',
              borderRadius: '4px',
              border: `1px solid ${permanentBlock ? '#d9534f' : '#e0a800'}`,
              backgroundColor: permanentBlock ? '#fdecea' : '#fff8e1',
              color: permanentBlock ? '#a02622' : '#7a5b00',
              fontSize: '13px',
            }}>
            <i className={permanentBlock ? 'dx-icon-clear' : 'dx-icon-clock'} style={{ fontSize: '18px', flexShrink: 0 }} />
            <span>
              {blockMessage}
              {!permanentBlock && remainingSeconds > 0 && (
                <>
                  {' '}
                  <strong>({formatRemaining(remainingSeconds)})</strong>
                </>
              )}
            </span>
          </div>
        )}

        <Form onInitialized={onInitialized} focusStateEnabled={true} ref={formRef} showValidationSummary={true} formData={formData} disabled={loading || isBlocked} showColonAfterLabel showRequiredMark={false}>
          <Item
            dataField='User'
            editorType='dxTextBox'
            editorOptions={{
              stylingMode: 'contained',
              labelMode: 'floating',
              placeholder: formatMessage('UserName'),
              // Opciones específicas para evitar autocompletado
              inputAttr: {
                autocomplete: 'off',
                'data-form-type': 'other',
                'data-lpignore': 'true', // Para LastPass
              },
            }}>
            <RequiredRule message={formatMessage('UserRequired')} />
            <Label visible={false} text={formatMessage('UserName')} />
          </Item>

          <Item
            dataField='Password'
            editorType='dxTextBox'
            editorOptions={{
              stylingMode: 'contained',
              labelMode: 'floating',
              placeholder: formatMessage('Password'),
              mode: showPassword ? 'text' : 'password',
              buttons: [
                {
                  name: 'password',
                  location: 'after',
                  options: {
                    stylingMode: 'text',
                    icon: 'eyeopen',
                    onClick: () => changePasswordMode(),
                  },
                },
              ],
              // Opciones específicas para evitar guardado de contraseña
              inputAttr: {
                autocomplete: 'new-password',
                'data-form-type': 'other',
                'data-lpignore': 'true', // Para LastPass
              },
            }}>
            <RequiredRule message={formatMessage('PasswordRequired')} />
            <Label visible={false} text={formatMessage('Password')} />
          </Item>

          {Locations && Locations.length > 1 && (
            <Item
              dataField='Campus'
              editorType='dxSelectBox'
              editorOptions={{
                searchTimeout: 0,
                items: lstLocations,
                displayExpr: 'Sd_Nombre',
                valueExpr: 'Sd_Id',
                searchEnabled: true,
                stylingMode: 'contained',
                labelMode: 'floating',
                placeholder: formatMessage('Location'),
                inputAttr: {
                  autocomplete: 'off',
                },
              }}>
              <RequiredRule message={formatMessage('LocationRequired')} />
              <Label visible={false} text={formatMessage('Location')} />
            </Item>
          )}

          <ButtonItem>
            <ButtonOptions width='100%' type='default' useSubmitBehavior disabled={(captchaRequired && !captchaToken) || isBlocked}>
              <span className='dx-button-text'>
                {loading ? <LoadIndicator width='24px' height='24px' visible /> : isBlocked && !permanentBlock ? formatRemaining(remainingSeconds) : formatMessage('ButtonLogin').toUpperCase()}
              </span>
            </ButtonOptions>
          </ButtonItem>
        </Form>
        {captchaRequired && (
          <div ref={turnstileRef} style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }} />
        )}
      </form>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '16px', width: '100%' }}>
        <strong style={{ fontSize: '12px', color: '#888' }}>{labName}</strong>
        <strong style={{ fontSize: '12px', color: '#888' }}>
          V. Core: {infoVersion} - App: {appVersion}
        </strong>
      </div>
    </div>
  );
};
